<div class="stepper-container">
  <mat-stepper [linear]="true" #stepper>

    <mat-step>
      <ng-template mat-step-label>Welcome</ng-template>
      <div class="step-content">
        <p class="welcome-text" *ngIf="calendarIntegrations.length === 0">
          Welcome to Yournotes. Before you begin, you need to set up a linked account.
        </p>
        <p>
          This account interacts with your accounts, like Google, iCal or CalDav.
          We will guide you through the process of connecting your account in the next pages.
        </p>

        <a href="/support" target="_blank" class="help-link">
          <mat-icon>help_outline</mat-icon>
          Need help connecting?
        </a>

        <div class="stepper-actions">
          <button mat-flat-button color="primary" matStepperNext>Get Started</button>
        </div>
      </div>
    </mat-step>

    <mat-step>
      <ng-template mat-step-label>Account Name</ng-template>
      <div class="step-content">
        <p>Create a logical name for your account, for example: <strong>"Work"</strong> or <strong>"Personal"</strong>.
        </p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <input matInput placeholder="e.g. Work Calendar" [(ngModel)]="calendarIntegration.description"
            name="description" required>
        </mat-form-field>

        <p>Setup the email address for this account
        </p>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Email Address</mat-label>
          <input matInput [formControl]="emailControl" placeholder="e.g. user@domain.com" type="email">

          <mat-error *ngIf="emailControl.invalid && (emailControl.dirty || emailControl.touched)">
            {{ getErrorMessage() }}
          </mat-error>
        </mat-form-field>

        <div class="stepper-actions">
          <button mat-button matStepperPrevious>Previous</button>
          <button mat-flat-button color="primary" (click)="validateDescription()">Next</button>
        </div>
      </div>
    </mat-step>

    <mat-step>
      <ng-template mat-step-label>Account type</ng-template>
      <div class="step-content">
        <p>Select the type of calendar you want to connect</p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Account type</mat-label>
          <mat-select [(ngModel)]="accountType" name="accountType" required
            [compareWith]="functions.objectComparisonFunction" (selectionChange)="selectAccountType(accountType)">
            <mat-option *ngFor="let accountType of accountTypes" [value]="accountType">
              {{accountType.description}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <div class="support-banner">
          <mat-icon>info</mat-icon>
          <div class="banner-text">
            <strong>Not sure which option to choose?</strong>
            <p>Read our step-by-step guide on how to link your specific calendar provider.</p>
          </div>
          <a href="/support" target="_blank" class="btn-support-link">
            Open Guide <mat-icon>open_in_new</mat-icon>
          </a>
        </div>

        <div class="stepper-actions">
          <button mat-button matStepperPrevious>Previous</button>
          <button mat-flat-button color="primary" (click)="validateAccountType()">Next</button>
        </div>
      </div>
    </mat-step>

    <!-- App Registration section -->
    <mat-step *ngIf="accountType?.code=='APP_REGISTRATION'">
      <ng-template mat-step-label>Provider</ng-template>
      <div class="step-content">
        <p>Select the app registration which links to the calendar you want to use.</p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>App registration</mat-label>
          <mat-select [(ngModel)]="appRegistration" name="appRegistration" required
            [compareWith]="functions.objectComparisonFunction"
            (selectionChange)="selectAppRegistration(appRegistration)">
            <mat-option *ngFor="let appReg of appRegistrations" [value]="appReg">
              {{appReg.description}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <div class="stepper-actions">
          <button mat-button matStepperPrevious>Previous</button>
          <button mat-flat-button color="primary" (click)="validateAppRegistration()">Next</button>
        </div>
      </div>
    </mat-step>

    <mat-step *ngIf="accountType?.code=='APP_REGISTRATION'">
      <ng-template mat-step-label>Connect</ng-template>
      <div class="step-content">
        <p>
          In the following steps, a popup from your provider will appear.
          Please log in and give consent to connect Yournotes to your calendar.
          You will be redirected back automatically.
        </p>

        <div class="stepper-actions">
          <button mat-button matStepperPrevious>Previous</button>

          <button *ngIf="provider !== 'Google'" mat-flat-button color="primary" (click)="requestConsent()">
            Connect Now
          </button>

          <img *ngIf="provider === 'Google'" class="google-btn" (click)="requestConsent()"
            src="assets/google_sign_in.png" alt="Sign in with Google">
        </div>
      </div>
    </mat-step>

    <!-- CALDAV section -->
    <mat-step *ngIf="accountType?.code=='CALDAV'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>Provider</ng-template>
      <div class="step-content">
        <p>Fill in the Server-URL, username and password for your CALDAV calendar</p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Server URL</mat-label>
          <input matInput placeholder="https://cloud.example.com/" [(ngModel)]="calDav.url" name="calDavUrl" required>
          <mat-icon matSuffix
            matTooltip="Enter the basic URL of your server in, like https://icloud.com or the Nextcloud address and we will figure out the rest."
            class="info-icon">
            info
          </mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Username</mat-label>
          <input matInput placeholder="Username" [(ngModel)]="calDav.username" name="username" required>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Password</mat-label>
          <input matInput [type]="hide ? 'password' : 'text'" placeholder="Password" [(ngModel)]="calDav.password"
            name="password" required #password="ngModel">
          <mat-icon matSuffix (click)="hide = !hide" class="pointer-icon">
            {{hide ? 'visibility_off' : 'visibility'}}
          </mat-icon>
          <mat-error *ngIf="password.invalid && (password.dirty || password.touched)">
            <span *ngIf="password.errors?.['required']">Password is required.</span>
          </mat-error>
        </mat-form-field>

        <div *ngIf="caldavFailed" class="alert-error" role="alert">
          CalDAV account validation failed
        </div>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="validateCalDav()">Next</button>
        </div>
      </div>

    </mat-step>


    <mat-step *ngIf="accountType?.code=='CALDAV'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>CalDav calendars</ng-template>
      <div class="step-content">
        <p>The CalDAV account is validated, please select which calendars you want to see in YourNotes and what color
          you want them to represent with.</p>

        <div class="table-container">
          <table mat-table [dataSource]="dataSource" class="full-width-table">
            <ng-container matColumnDef="Title">
              <th mat-header-cell *matHeaderCellDef> Title </th>
              <td mat-cell *matCellDef="let element"> {{element.summary}} </td>
            </ng-container>

            <ng-container matColumnDef="Show in Yournotes">
              <th mat-header-cell *matHeaderCellDef> Show in Yournotes </th>
              <td mat-cell *matCellDef="let element">
                <mat-checkbox [(ngModel)]="element.showInYournotes" color="primary"></mat-checkbox>
              </td>
            </ng-container>

            <ng-container matColumnDef="Calendar color">
              <th mat-header-cell *matHeaderCellDef> Color </th>
              <td mat-cell *matCellDef="let element">
                <app-color-picker [selectedColor]="element.color" (colorChange)="element.color = $event">
                </app-color-picker>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="saveICalDavFlow()">Save</button>
        </div>
      </div>

    </mat-step>

    <mat-step *ngIf="accountType?.code=='CALDAV'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>CalDav configuration</ng-template>
      <div class="step-content">
        <p>The CalDAV account is validated and all is set, press Save to store the configuration</p>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="saveICalDavFlow()">Save</button>
        </div>
      </div>

    </mat-step>


    <!-- iCal section -->
    <mat-step *ngIf="accountType?.code=='ICAL'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>iCal calendar</ng-template>
      <div class="step-content">
        <p>Fill in the URL for your iCal calendar</p>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>URL</mat-label>
          <input matInput placeholder="iCal URL" [(ngModel)]="iCal.url" name="iCalUrl" required>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Calendar name</mat-label>
          <input matInput placeholder="Calendar name" [(ngModel)]="calendarName" name="calendarName" required>
        </mat-form-field>

        <div *ngIf="iCalError" class="alert-error" role="alert">
          {{ iCalErrorMessage }}
        </div>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="validate_iCal()">Next</button>
        </div>
      </div>

    </mat-step>

    <mat-step *ngIf="accountType?.code=='ICAL'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>iCal configuration</ng-template>
      <div class="step-content">
        <p>The url is validated and all is set, press Save to store the configuration</p>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="saveICalDavFlow()">Save</button>
        </div>
      </div>

    </mat-step>

    <mat-step *ngIf="accountType?.code=='YOURNOTES'">
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading"></mat-progress-bar>
      <ng-template mat-step-label>YourNotes configuration</ng-template>
      <div class="step-content">
        <p>All is set, press Save to store the configuration</p>

        <div class="stepper-actions">
          <button mat-button [disabled]="isLoading" matStepperPrevious>Previous</button>
          <button mat-flat-button [disabled]="isLoading" color="primary" (click)="saveYourNotesConfig()">Save</button>
        </div>
      </div>

    </mat-step>

  </mat-stepper>

</div>